import { Document, Font, Image, Page, pdf, StyleSheet, Text, View } from '@react-pdf/renderer';
import React, { useEffect, useState } from 'react';
import { ProductDataResult } from '../../Models/ProductData';

// Use CDN-hosted fonts instead of local files
Font.register({
    family: 'Helvetica',
    fonts: [
        { src: 'https://cdn.jsdelivr.net/npm/@canvas-fonts/helvetica@1.0.4/Helvetica.ttf' },
        { src: 'https://cdn.jsdelivr.net/npm/@canvas-fonts/helvetica@1.0.4/Helvetica-Bold.ttf', fontWeight: 'bold' },
    ]
});

type ModalExportProps = {
    generatedBom: Array<ProductDataResult>,
    recommendedProducts: Array<ProductDataResult>,
    additionalProducts: Array<ProductDataResult>
}

const DownloadPDFButton = (props: ModalExportProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Define your styles here (as in your original component)
    const styles = StyleSheet.create({
        page: {
            backgroundColor: '#fff',
            fontFamily: 'Helvetica',
            fontSize: 11,
            paddingTop: 30,
            paddingLeft: 30,
            paddingRight: 30,
            lineHeight: 1.5,
            flexDirection: 'column',
            paddingBottom: 50
        },
        // Include the rest of your styles...
        logo: {
            height: '100px',
            width: '100px',
            marginBottom: -20,
            marginLeft: 230
        },
        row: { display: "flex", flexDirection: "row", fontSize: 9, width: '100%' },
        column: {
            display: "flex", flexDirection: "column", width: '100%', paddingTop: 12,
            paddingBottom: 8
        },
        table: { width: '100%', marginBottom: 20 },
        tableAdditionalProducts: { width: '100%', marginBottom: 20 },
        tableRecommendedProducts: { width: '100%', marginBottom: 20 },
        bold: { fontWeight: 'bold' },
        headerColumn1: {
            width: '20%', borderTop: '1px solid #000000', borderLeft: '1px solid #000000',
            borderRight: '1px solid #000000', textAlign: 'left', paddingTop: '5px',
            paddingLeft: '3px', borderBottom: '1px solid #000000', fontWeight: 'bold'
        },
        headerColumn2: {
            width: '15%', borderTop: '1px solid #000000', borderRight: '1px solid #000000',
            textAlign: 'left', paddingTop: '5px', borderBottom: '1px solid #000000',
            fontWeight: 'bold', paddingLeft: '3px'
        },
        headerColumn3: {
            width: '7%', borderTop: '1px solid #000000', borderRight: '1px solid #000000',
            textAlign: 'left', paddingTop: '5px', borderBottom: '1px solid #000000',
            fontWeight: 'bold', paddingLeft: '3px'
        },
        headerColumn4: {
            width: '58%', borderTop: '1px solid #000000', borderRight: '1px solid #000000',
            textAlign: 'left', paddingTop: '5px', borderBottom: '1px solid #000000',
            fontWeight: 'bold', paddingLeft: '3px'
        },
        column1: {
            width: '20%', borderLeft: '1px solid #000000', borderRight: '1px solid #000000',
            borderBottom: '1px solid #000000', textAlign: 'left', paddingTop: 2, paddingLeft: '3px'
        },
        column2: {
            width: '15%', borderRight: '1px solid #000000', borderBottom: '1px solid #000000',
            textAlign: 'left', paddingTop: 2, paddingLeft: '3px'
        },
        column3: {
            width: '7%', borderRight: '1px solid #000000', borderBottom: '1px solid #000000',
            textAlign: 'left', paddingTop: 2, paddingLeft: '3px'
        },
        column4: {
            width: '58%', borderRight: '1px solid #000000', borderBottom: '1px solid #000000',
            textAlign: 'left', paddingTop: 2, paddingLeft: '3px'
        }
    });

    // Create PDF document definition
    const createPdfDocument = () => (
        <Document>
            <Page size="A4" style={styles.page} wrap={true}>
                <View style={styles.row}>
                    <View>
                        <Text style={{ fontSize: "15px", color: "#0039c1", fontWeight: 'bold', marginTop: -15 }}>
                            Constellation Building Edge Infrastructure
                        </Text>
                    </View>
                    <View style={styles.column}>
                        <View style={styles.logo}>
                            <Image style={{ marginTop: -26 }} src="CS_Amphenol_Black.png" />
                        </View>
                    </View>
                </View>

                <View style={{ marginBottom: 20, marginTop: -70 }}>
                    <Text style={{ fontSize: "6px", fontWeight: 'light', marginTop: 10 }}>
                        DISCLAIMER: These calculations are provided for guidance purposes only. They
                        are provided as is and should not be relied upon without consultation with
                        and supervision of experienced personnel and a local CommScope
                        representative. CommScope makes no representations or warranties of any kind
                        related to these tools, whether express or implied, and specifically
                        disclaims and excludes any representation or warranty of merchantability,
                        quality, content, completeness, suitability, adequacy, accuracy,
                        noninfringement or fitness for a particular purpose and any representation
                        arising by usage of trade, course of dealing or course of performance.
                        CommScope is under no obligation to issue any upgrades, update
                        specifications or notify users of this tool that changes have been made. The
                        user of these tools assumes all risks associated with such use, and
                        CommScope hereby disclaims any and all liability for damages of any kind
                        resulting from such use.
                    </Text>
                </View>

                <View style={{ marginTop: 0, marginBottom: 15 }}>
                    <Text style={{ fontSize: "15px", color: "#0039c1", fontWeight: 'bold' }}>
                        Constellation Bill of Materials
                    </Text>
                </View>

                <View style={styles.table} wrap={true}>
                    <View style={[styles.row, styles.bold]}>
                        <Text style={styles.headerColumn1}>Part Number</Text>
                        <Text style={styles.headerColumn2}>Length (meters)</Text>
                        <Text style={styles.headerColumn3}>Qty</Text>
                        <Text style={styles.headerColumn4}>Description</Text>
                    </View>

                    {props.generatedBom.map((bomProduct: ProductDataResult, index) => (
                        <View key={`bom-${index}`} style={styles.row}>
                            <View style={styles.column1}>
                                <Text>{bomProduct.partNumber || bomProduct.title || ""}</Text>
                            </View>
                            <View style={styles.column2}>
                                <Text>{bomProduct.length > 0 ? bomProduct.length.toString() : '-'}</Text>
                            </View>
                            <View style={styles.column3}>
                                <Text>{bomProduct.quantity > 0 ? bomProduct.quantity.toString() : '-'}</Text>
                            </View>
                            <View style={styles.column4}>
                                <Text>{bomProduct.summary || ""}</Text>
                            </View>
                        </View>
                    ))}
                </View>

                <View style={{ marginTop: 0, marginBottom: 15 }}>
                    <Text style={{ fontSize: "15px", color: "#0039c1", fontWeight: 'bold' }}>
                        Recommended Products
                    </Text>
                </View>

                <View style={styles.tableRecommendedProducts} wrap={true}>
                    <View style={[styles.row, styles.bold]}>
                        <Text style={styles.headerColumn1}>Part Number</Text>
                        <Text style={styles.headerColumn2}>Length (meters)</Text>
                        <Text style={styles.headerColumn3}>Qty</Text>
                        <Text style={styles.headerColumn4}>Description</Text>
                    </View>

                    {props.recommendedProducts.map((product: ProductDataResult, index) => (
                        <View key={`rec-${index}`} style={styles.row}>
                            <View style={styles.column1}>
                                <Text>{product.partNumber || product.title || ""}</Text>
                            </View>
                            <View style={styles.column2}>
                                <Text>{product.length > 0 ? product.length.toString() : '-'}</Text>
                            </View>
                            <View style={styles.column3}>
                                <Text>{product.quantity > 0 ? product.quantity.toString() : '-'}</Text>
                            </View>
                            <View style={styles.column4}>
                                <Text>{product.summary || ""}</Text>
                            </View>
                        </View>
                    ))}
                </View>

                {props.additionalProducts && props.additionalProducts.length > 0 && (
                    <>
                        <View style={{ marginBottom: 15, marginTop: 0 }}>
                            <Text style={{ fontSize: "15px", color: "#0039c1", fontWeight: 'bold' }}>
                                Additional Products
                            </Text>
                        </View>

                        <View style={styles.tableAdditionalProducts} wrap={true}>
                            <View style={[styles.row, styles.bold]}>
                                <Text style={styles.headerColumn1}>Part Number</Text>
                                <Text style={styles.headerColumn2}>Length (meters)</Text>
                                <Text style={styles.headerColumn3}>Qty</Text>
                                <Text style={styles.headerColumn4}>Description</Text>
                            </View>

                            {props.additionalProducts.map((product: ProductDataResult, index) => (
                                <View key={`add-${index}`} style={styles.row}>
                                    <View style={styles.column1}>
                                        <Text>{product.partNumber || product.title || ""}</Text>
                                    </View>
                                    <View style={styles.column2}>
                                        <Text>{product.length > 0 ? product.length.toString() : '-'}</Text>
                                    </View>
                                    <View style={styles.column3}>
                                        <Text>{product.quantity > 0 ? product.quantity.toString() : '-'}</Text>
                                    </View>
                                    <View style={styles.column4}>
                                        <Text>{product.summary || ""}</Text>
                                    </View>
                                </View>
                            ))}
                        </View>
                    </>
                )}

                <View style={{ position: 'absolute', bottom: 20,left:30,right:30}} fixed>
                    <Text style={{
                        fontSize: "6", textAlign: 'right', lineHeight: 1.3}}>
                        {"\u00A9"}2026 CommScope Technologies LLC, an Amphenol company. All rights reserved.
                        CommScope and the CommScope logo are registered trademarks of CommScope and/or its affiliates in the U.S. and other countries.
                        For additional trademark information see https://www.commscope.com/trademarks.
                        All product names, trademarks and registered trademarks are property of their respective owners.
                    </Text>
                </View>
            </Page>
        </Document>
    );


    // Manual PDF generation and download handler
    const handleDownload = async () => {
        if (typeof window === 'undefined') {
            return; // Server-side check
        }

        try {
            setIsLoading(true);
            setError(null);

            // Create the document
            const pdfDocument = createPdfDocument();

            // Generate PDF blob
            const blob = await pdf(pdfDocument).toBlob();

            // Create and trigger download
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'ConstellationBoM.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error generating PDF:', err);
            setError(err instanceof Error ? err.message : 'Unknown error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className='download-link ml-4'>
            <button
                onClick={handleDownload}
                disabled={isLoading}
                className="btn btn-primary">
                {isLoading ? 'Preparing document...' : 'Save as PDF'}
            </button>
            {error && (
                <div className="text-danger mt-2 small">
                    Error: {error}
                </div>
            )}
        </div>
    );
};

export default DownloadPDFButton;